import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Cell,
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4'];

function Backdrop({ onClose, children }) {
  return (
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 2000,
      }}
    >
      {children}
    </div>
  );
}

export default function ReportChartsModal({
  open,
  onClose,
  title = 'Gráficos',
  presets = [],
}) {
  const [presetId, setPresetId] = useState(presets?.[0]?.id || '');

  const preset = useMemo(() => {
    const p = (presets || []).find(x => x.id === presetId);
    return p || presets?.[0] || null;
  }, [presets, presetId]);

  if (!open) return null;

  return (
    <Backdrop onClose={onClose}>
      <div
        style={{
          width: 'min(1100px, 96vw)',
          height: 'min(720px, 92vh)',
          background: '#fff',
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 18px 60px rgba(0,0,0,.35)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: 14, borderBottom: '1px solid #eef2f7', background: '#fafafa', display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 900, color: '#0f172a' }}>📊 {title}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Escolha um gráfico e explore os dados atuais.</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select
              value={preset?.id || ''}
              onChange={(e) => setPresetId(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #e5e7eb', fontWeight: 700 }}
            >
              {(presets || []).map(p => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 800 }}
            >
              ✕ Fechar
            </button>
          </div>
        </div>

        <div style={{ padding: 14, flex: 1, minHeight: 0 }}>
          {!preset ? (
            <div style={{ color: '#64748b' }}>Sem presets de gráfico.</div>
          ) : (
            <div style={{ width: '100%', height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                {preset.type === 'bar' ? (
                  <BarChart data={preset.data || []} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={preset.xKey} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={preset.yKey} fill="#3b82f6" name={preset.yLabel || preset.yKey} />
                  </BarChart>
                ) : preset.type === 'line' ? (
                  <LineChart data={preset.data || []} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={preset.xKey} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey={preset.yKey} stroke="#10b981" strokeWidth={3} dot={false} name={preset.yLabel || preset.yKey} />
                  </LineChart>
                ) : (
                  <PieChart>
                    <Tooltip />
                    <Legend />
                    <Pie
                      data={preset.data || []}
                      dataKey={preset.yKey}
                      nameKey={preset.xKey}
                      cx="50%"
                      cy="50%"
                      outerRadius={220}
                      label
                    >
                      {(preset.data || []).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </Backdrop>
  );
}
